/**
 * Cronologiile flotei, derivate din procesele-verbale semnate (task CRM #2445):
 *  - pagina masinii: cine a avut-o, pe perioade, cu consumul fiecaruia si golurile fara PV;
 *  - pagina soferului: masinile lui, pe perioade;
 *  - „Masina mea": acelasi lucru pentru omul logat, fara acces la restul flotei.
 */
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, Car, FileText, User } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '../../../lib/api'
import { deschideFisier } from '../../../lib/downloadFile'
import { AntetFix } from '../../../components/ui/AntetFix'
import { Pastila } from '../../../components/ui/Pastila'
import { useAuthStore } from '../../../stores/auth'
import {
  LinkSofer,
  StarePv,
  cand,
  linkPvNou,
  mesajEroare,
  ron,
  type Consum,
  type PvRand,
} from './flotaComun'

interface PerioadaMasina {
  de: string
  pana: string | null
  detinator: number | null
  numeDetinator: string
  pvDeLaId: number
  pvPanaLaId: number | null
  numarPv: string | null
  concordanta: boolean
  consum: Consum
}
interface Gol {
  de: string
  pana: string
  nrTx: number
  suma: number
  motiv: 'fara_pv' | 'neconcordant'
}

function Consumul({ c }: { c: Consum }) {
  if (!c.nrTx) return <span className="text-slate-400">fără alimentări</span>
  return (
    <span>
      {ron(c.cost)} · {c.litri.toLocaleString('ro-RO')} L · {c.nrTx}{' '}
      {c.nrTx === 1 ? 'alimentare' : 'alimentări'}
    </span>
  )
}

export function MasinaPage() {
  const { id } = useParams<{ id: string }>()
  const poateScrie = useAuthStore((s) => s.permissions.includes('operational.flota_pv:write'))
  const q = useQuery({
    queryKey: ['flota', 'cronologie', id],
    queryFn: async () =>
      (
        await api.get<{
          masina: {
            id: number
            plate: string
            brand: string | null
            model: string | null
            driverName: string | null
          }
          perioade: PerioadaMasina[]
          goluri: Gol[]
        }>(`/flota/masini/${id}/cronologie`)
      ).data,
  })
  const pv = useQuery({
    queryKey: ['flota', 'pv', 'masina', id],
    queryFn: async () =>
      (await api.get<{ rows: PvRand[] }>('/flota/pv', { params: { vehicleId: id } })).data.rows,
  })

  if (q.isLoading) return <div className="p-4 text-slate-500">Se încarcă…</div>
  if (q.isError || !q.data) return <div className="p-4 text-rose-600">{mesajEroare(q.error)}</div>
  const { masina, perioade, goluri } = q.data
  const inOrdine = [...perioade].reverse()

  return (
    <div className="space-y-4">
      <AntetFix>
        <div className="flex flex-wrap items-center gap-2">
          <Car className="h-6 w-6 text-orange-500" />
          <h1 className="font-mono text-2xl font-bold">{masina.plate}</h1>
          {masina.brand && (
            <span className="text-slate-500">
              {masina.brand} {masina.model}
            </span>
          )}
          <Link
            to="/operational/rompetrol"
            className="ml-2 text-sm text-slate-500 hover:text-slate-700"
          >
            ← Flotă Rompetrol
          </Link>
          {poateScrie && (
            <Link
              to={linkPvNou({
                vehicleId: masina.id,
                predatorId: perioade[perioade.length - 1]?.detinator ?? null,
              })}
              className="ml-auto rounded-lg bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600"
            >
              Predă mașina (PV nou)
            </Link>
          )}
        </div>
        <p className="text-sm text-slate-500">
          Cine a avut mașina, după procesele-verbale semnate.
          {masina.driverName &&
            ` Șoferul trecut de mână pe mașină: ${masina.driverName} (doar informativ).`}
        </p>
      </AntetFix>

      {goluri.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <div className="mb-2 flex items-center gap-2 font-medium">
            <AlertTriangle className="h-4 w-4" /> Alimentări nealocate — lipsește PV-ul
          </div>
          <ul className="space-y-1">
            {goluri.map((g, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2">
                <span>
                  {cand(g.de)} – {cand(g.pana)}: {g.nrTx}{' '}
                  {g.nrTx === 1 ? 'alimentare' : 'alimentări'}, {ron(g.suma)}
                  {g.motiv === 'neconcordant' && ' (două PV-uri consecutive nu se leagă)'}
                </span>
                {poateScrie && (
                  <Link
                    to={linkPvNou({ vehicleId: masina.id, la: g.de })}
                    className="font-medium text-orange-700 underline"
                  >
                    Completează PV-ul
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">De la</th>
              <th className="px-3 py-2">Până la</th>
              <th className="px-3 py-2">A avut-o</th>
              <th className="px-3 py-2">PV</th>
              <th className="px-3 py-2">Consum în perioadă</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {inOrdine.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-slate-500">
                  Niciun PV semnat pe mașina asta: toate alimentările ei sunt nealocate.
                </td>
              </tr>
            )}
            {inOrdine.map((p) => (
              <tr key={p.pvDeLaId} className={p.concordanta ? '' : 'bg-amber-50'}>
                <td className="px-3 py-2">{cand(p.de)}</td>
                <td className="px-3 py-2">
                  {p.pana ? cand(p.pana) : <Pastila ton="verde">încă la el</Pastila>}
                </td>
                <td className="px-3 py-2">
                  {p.concordanta ? (
                    <LinkSofer id={p.detinator} nume={p.numeDetinator} />
                  ) : (
                    <span className="text-amber-800">
                      neclar — PV-ul următor e predat de altcineva decât {p.numeDetinator}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono">
                  <Link
                    to={`/operational/flota/pv/${p.pvDeLaId}`}
                    className="text-orange-700 hover:underline"
                  >
                    {p.numarPv ?? `#${p.pvDeLaId}`}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <Consumul c={p.consum} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ListaPv rows={pv.data ?? []} />
    </div>
  )
}

interface PerioadaSofer {
  vehicleId: number
  de: string
  pana: string | null
  pvDeLaId: number
  pvPanaLaId: number | null
  numarPvDeLa: string | null
  numarPvPanaLa: string | null
  masina: { id: number; plate: string; brand: string | null; model: string | null } | null
  consum: Consum
}

function TabelSofer({ perioade, cuLinkuri }: { perioade: PerioadaSofer[]; cuLinkuri: boolean }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2">Mașina</th>
            <th className="px-3 py-2">De la</th>
            <th className="px-3 py-2">Până la</th>
            <th className="px-3 py-2">Consum în perioadă</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {perioade.length === 0 && (
            <tr>
              <td colSpan={4} className="px-3 py-6 text-slate-500">
                Nicio mașină pe procese-verbale semnate.
              </td>
            </tr>
          )}
          {perioade.map((p) => (
            <tr key={p.pvDeLaId}>
              <td className="px-3 py-2 font-mono font-medium">
                {cuLinkuri ? (
                  <Link to={`/operational/flota/masini/${p.vehicleId}`} className="hover:underline">
                    {p.masina?.plate}
                  </Link>
                ) : (
                  p.masina?.plate
                )}
                {p.masina?.brand && (
                  <span className="ml-1 font-sans font-normal text-slate-500">
                    {p.masina.brand} {p.masina.model}
                  </span>
                )}
              </td>
              <td className="px-3 py-2">
                {cand(p.de)}
                {p.numarPvDeLa && (
                  <span className="ml-1 font-mono text-xs text-slate-400">{p.numarPvDeLa}</span>
                )}
              </td>
              <td className="px-3 py-2">
                {p.pana ? cand(p.pana) : <Pastila ton="verde">încă la el</Pastila>}
                {p.numarPvPanaLa && (
                  <span className="ml-1 font-mono text-xs text-slate-400">{p.numarPvPanaLa}</span>
                )}
              </td>
              <td className="px-3 py-2">
                <Consumul c={p.consum} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function SoferPage() {
  const { userId } = useParams<{ userId: string }>()
  const q = useQuery({
    queryKey: ['flota', 'sofer', userId],
    queryFn: async () =>
      (
        await api.get<{
          user: { id: number; name: string; jobTitle: string | null }
          perioade: PerioadaSofer[]
        }>(`/flota/soferi/${userId}`)
      ).data,
  })
  const pv = useQuery({
    queryKey: ['flota', 'pv', 'om', userId],
    queryFn: async () =>
      (await api.get<{ rows: PvRand[] }>('/flota/pv', { params: { userId } })).data.rows,
  })
  if (q.isLoading) return <div className="p-4 text-slate-500">Se încarcă…</div>
  if (q.isError || !q.data) return <div className="p-4 text-rose-600">{mesajEroare(q.error)}</div>
  return (
    <div className="space-y-4">
      <AntetFix>
        <div className="flex flex-wrap items-center gap-2">
          <User className="h-6 w-6 text-orange-500" />
          <h1 className="text-2xl font-bold">{q.data.user.name}</h1>
          {q.data.user.jobTitle && <span className="text-slate-500">{q.data.user.jobTitle}</span>}
          <Link
            to="/operational/rompetrol"
            className="ml-2 text-sm text-slate-500 hover:text-slate-700"
          >
            ← Flotă Rompetrol
          </Link>
        </div>
        <p className="text-sm text-slate-500">
          Mașinile pe care le-a avut, după procesele-verbale semnate.
        </p>
      </AntetFix>
      <TabelSofer perioade={q.data.perioade} cuLinkuri />
      <ListaPv rows={pv.data ?? []} />
    </div>
  )
}

/** „Mașina mea": doar mașinile și PV-urile omului logat. */
export function MasinaMeaPage() {
  const q = useQuery({
    queryKey: ['flota', 'ale-mele'],
    queryFn: async () =>
      (await api.get<{ perioade: PerioadaSofer[]; pv: PvRand[] }>('/flota/ale-mele')).data,
  })
  return (
    <div className="space-y-4">
      <AntetFix>
        <div className="flex items-center gap-2">
          <Car className="h-6 w-6 text-orange-500" />
          <h1 className="text-2xl font-bold">Mașina mea</h1>
        </div>
        <p className="text-sm text-slate-500">
          Mașinile firmei pe care le-ai avut și procesele-verbale semnate de tine. Dacă lipsește
          ceva, spune-i celui care se ocupă de flotă.
        </p>
      </AntetFix>
      {q.isLoading ? (
        <div className="text-slate-500">Se încarcă…</div>
      ) : q.isError || !q.data ? (
        <div className="text-rose-600">{mesajEroare(q.error)}</div>
      ) : (
        <>
          <TabelSofer perioade={q.data.perioade} cuLinkuri={false} />
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">Nr.</th>
                  <th className="px-3 py-2">Mașină</th>
                  <th className="px-3 py-2">Predare</th>
                  <th className="px-3 py-2">Predă → Primește</th>
                  <th className="px-3 py-2">Documente</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {q.data.pv.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-slate-500">
                      Niciun proces-verbal semnat pe numele tău.
                    </td>
                  </tr>
                )}
                {q.data.pv.map((p) => (
                  <tr key={p.id}>
                    <td className="px-3 py-2 font-mono">{p.numar}</td>
                    <td className="px-3 py-2 font-mono">{p.plate}</td>
                    <td className="px-3 py-2">{cand(p.predatLa)}</td>
                    <td className="px-3 py-2">
                      {p.numePredator} → {p.numePrimitor}
                    </td>
                    <td className="space-x-3 px-3 py-2">
                      <Deschide url={`/flota/ale-mele/pv/${p.id}/pdf`} eticheta="PDF" />
                      {p.areScan && (
                        <Deschide
                          url={`/flota/ale-mele/pv/${p.id}/scan`}
                          eticheta="Scanul semnat"
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function Deschide({ url, eticheta }: { url: string; eticheta: string }) {
  return (
    <button
      onClick={() => void deschideFisier(url).catch((e) => toast.error(mesajEroare(e)))}
      className="inline-flex items-center gap-1 text-orange-700 hover:underline"
    >
      <FileText className="h-3.5 w-3.5" /> {eticheta}
    </button>
  )
}

function ListaPv({ rows }: { rows: PvRand[] }) {
  if (!rows.length) return null
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-2 text-sm font-medium text-slate-700">Procese-verbale</div>
      <ul className="space-y-1 text-sm">
        {rows.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center gap-2">
            <Link
              to={`/operational/flota/pv/${p.id}`}
              className="font-mono text-orange-700 hover:underline"
            >
              {p.numar ?? `ciornă #${p.id}`}
            </Link>
            <span className="font-mono">{p.plate}</span>
            <span>{cand(p.predatLa)}</span>
            <span>
              {p.numePredator} → {p.numePrimitor}
            </span>
            <StarePv status={p.status} />
          </li>
        ))}
      </ul>
    </div>
  )
}
